<template>
  <admin-page-layout>
    <template #titleName> Exports des ventes </template>

    <template #titleAction>
      <div class="flex items-center gap-x-4">
        <FormKit
          type="select"
          name="exportType"
          v-model="selectedExportType"
          validation="required"
          :options="exportTypesOptions"
          :validation-messages="{
            required: 'Veuillez choisir un type d\'export.',
          }"
        />
      </div>
    </template>

    <template #content>
      <FormKit
        type="form"
        id="exportForm"
        :actions="false"
        @submit="downloadCsvFile"
        v-model="formValues"
        #default="{ state: { valid } }"
      >
        <div class="container mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-x-4">
            <div class="col">
              <FormKit
                type="datepicker"
                name="startDate"
                label="Date de début*"
                validation="required|checkDatesIntervalRules"
                :validation-messages="{
                  required: 'Date obligatoire.',
                  checkDatesIntervalRules:
                    'La date de début doit être inférieur ou égale à la date de fin.',
                }"
                validation-visibility="live"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                v-model="startDate"
              />
            </div>
            <div class="col">
              <FormKit
                type="datepicker"
                name="endDate"
                label="Date de fin*"
                validation="required|checkDatesIntervalRules"
                :validation-messages="{
                  required: 'Date obligatoire.',
                  checkDatesIntervalRules:
                    'La date de fin doit être supérieure ou égale à la date de début.',
                }"
                validation-visibility="live"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                v-model="endDate"
              />
            </div>
            <div class="col" v-if="exportType.hasSupplierFilter">
              <FormKit
                type="autocomplete"
                label="Fournisseur"
                name="supplier"
                placeholder="Sélectionner un fournisseur"
                :options="searchSupplierOptions"
                v-model="supplier"
              />
            </div>
            <div class="col">
              <div class="flex items-center mt-5">
                <FormKit
                  type="submit"
                  label="Exporter"
                  :class="{ 'btn-secondary': true, 'btn-block': true }"
                  :disabled="!valid || isLoading"
                />
              </div>
            </div>
          </div>
          <div>
            <p class="mb-4 mt-2 text-slate-600">
              {{ exportType.description }}
            </p>
            <CodeViewer :code="exportTypeSql" :details="filters" />
          </div>
        </div>
      </FormKit>
    </template>
  </admin-page-layout>
</template>

<script setup lang="ts">
import AdminPageLayout from "@/layouts/default/admin/AdminPageLayout.vue";
import { ref, onMounted, computed, reactive } from "vue";
import { getTodayDate, getFirstDayOfMonth } from "@/_helpers/dateManipulations";
import {
  generateFileName,
  downloadFile,
  generateFileUrl,
} from "@/_helpers/fileManipulations";
import { useToast } from "primevue/usetoast";
import { searchSupplierOptions } from "@/_services/supplier/supplier";
import { exportTypes } from "@/constants/export/exports";
import { exportTypesSql } from "@/constants/export/exportsSql";
import CodeViewer from "@/components/utility/CodeViewer";

const startDate = ref<string>("");
const endDate = ref<string>("");
const selectedExportType = ref<string>("sold_documents");
const exportFileExtension = ".csv";
const isLoading = ref<boolean>(true);
const supplier = ref<string>("");
const toast = useToast();
const formValues = reactive({});

const exportTypesOptions = computed(() => {
  return Object.keys(exportTypes).map((key) => ({
    value: key,
    label: exportTypes[key].label,
  }));
});

const exportType = computed(() => {
  return exportTypes[selectedExportType.value];
});

const exportTypeSql = computed(() => {
  return exportTypesSql[selectedExportType.value];
});

// This is necessary to fix a bug on code viewer details display
const filters = computed(() => {
  return formValues;
})

onMounted(() => {
  startDate.value = getFirstDayOfMonth();
  endDate.value = getTodayDate();
  isLoading.value = false;
});

const downloadCsvFile = async () => {
  isLoading.value = true;

  try {
    toast.add({
      severity: "warn",
      summary: "Attention",
      detail: "Veuillez ne pas quitter la page pendant l'export",
      life: 4000,
    });

    const csvData: string = await exportType.value["callback"](
      ...Object.values(formValues)
    );
    const fileName = generateFileName(
      exportType.value["fileNamePrefix"],
      exportFileExtension
    );
    const url = generateFileUrl(csvData);
    downloadFile(fileName, url);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Erreur",
      detail: "Erreur lors du téléchargement",
      life: 4000,
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

